import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

const ENDPOINT = '/default-associate-name'

export const QUERY_KEYS = {
  root: ['default-associate-name'],
  portfolios: (accountId) => ['default-associate-name', 'portfolios', String(accountId || '')],
  accounts: ['default-associate-name', 'accounts'],
}

export function useDefaultAssociateNameData() {
  const [selectedPortfolioAccountId, setSelectedPortfolioAccountId] = useState('')

  const accountsQuery = useQuery({
    queryKey: QUERY_KEYS.accounts,
    queryFn: () => api.get(`${ENDPOINT}/accounts`),
    staleTime: 5 * 60 * 1000,
  })

  const portfoliosQuery = useQuery({
    queryKey: QUERY_KEYS.portfolios(selectedPortfolioAccountId),
    queryFn: () =>
      api.get(
        `${ENDPOINT}/portfolios?accountId=${encodeURIComponent(selectedPortfolioAccountId)}`,
      ),
    enabled: Boolean(selectedPortfolioAccountId),
    staleTime: 2 * 60 * 1000,
  })

  return {
    accounts: accountsQuery.data?.accounts || [],
    portfolios: portfoliosQuery.data?.portfolios || [],
    selectedPortfolioAccountId,
    setSelectedPortfolioAccountId,
    isAccountsLoading: accountsQuery.isLoading,
    isPortfoliosLoading: Boolean(selectedPortfolioAccountId) && portfoliosQuery.isLoading,
    isFetching: portfoliosQuery.isFetching || accountsQuery.isFetching,
    error: portfoliosQuery.error || accountsQuery.error,
    refetch: () => {
      accountsQuery.refetch()
      if (selectedPortfolioAccountId) {
        portfoliosQuery.refetch()
      }
    },
  }
}

export function useDefaultAssociateNameMutations() {
  const queryClient = useQueryClient()

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.root })
  }

  const updatePortfoliosMutation = useMutation({
    mutationFn: ({ accountId, portfolioIds, residentAdvocateFirstName }) =>
      api.put(`${ENDPOINT}/portfolios`, {
        accountId,
        portfolioIds,
        residentAdvocateFirstName,
      }),
    onSuccess: invalidate,
  })

  const updateAccountMutation = useMutation({
    mutationFn: ({ accountId, residentAdvocateFirstName }) =>
      api.put(`${ENDPOINT}/accounts/${accountId}`, {
        residentAdvocateFirstName,
      }),
    onSuccess: invalidate,
  })

  return {
    updatePortfoliosMutation,
    updateAccountMutation,
  }
}

export function useSelectedAccount(accounts, selectedAccountId) {
  return useMemo(
    () => accounts.find((account) => String(account.accountId) === String(selectedAccountId)),
    [accounts, selectedAccountId],
  )
}
